import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Container, Dialog, Slide } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { ApiService, IndividualQuestionBaseRepresentation, RequestedFile } from 'api';
import { DOCUMENT_REQUEST_CATEGORY, QUESTIONNAIRE_REQUEST_CATEGORY } from 'common/constants/process-management';
import { ALLOWED_LIMITED_VIEWER_ROLES_FOR_DOCS } from 'common/constants/user';
import { useInformationRequestStatuses } from 'common/hooks/useInformationRequestStatuses';
import { useStore } from 'common/store';
import { CompanyByFirm } from 'common/types/company';
import { UseStoreValues } from 'common/types/store';
import { UserFirmPermission } from 'common/types/user';
import { InformationDashboard409AContext } from 'context/InformationDashboard409AContext';
import { MeasurementDate, ReportDate } from 'dashboard409a/services/types';
import { IStandaloneCompaniesPermissions } from 'dashboard409a/states/features/user';
import {
  getDefaultInformationRequestExpirationDate,
  getInformationRequestExpirationFromRequests,
  isValidInformationRequestExpirationDate,
  parseInformationRequestExpirationDate,
  toInformationRequestExpirationIso,
} from 'dashboard409a/utilities';
import { manageApiRequest } from 'hooks/apiRequest';
import RequestsContent from 'pages/Firms/Requests/components/RequestContent/RequestContent';
import useGetCompanyMeasurementDateByMD from 'services/hooks/companies/useGetCompanyMeasurementDateByMD/useGetCompanyMeasurementDateByMD';
import useGetCompanyUsers from 'services/hooks/useGetCompanyUsers';
import { useGetFirmUsersSlim } from 'services/hooks/useGetFirmUsers';
import { Logger } from 'utilities';
import ConfirmRequest from './components/ConfirmRequest';
import { CreateInformationRequestModalType } from './CreateInformationRequestModal.props';
import { SourceResponsibleSelector } from '../InformationRequestModal/SourceResponsibleSelector';
import { ResponsibleUser } from '../InformationRequestModal/SourceResponsibleSelector/types';
import { mergeResponsibleUsers } from '../InformationRequestModal/SourceResponsibleSelector/utils';
import InformationRequestModalAppBar from './components/InformationRequestModalAppBar/InformationRequestModalAppBar';

type RequestItem = RequestedFile | IndividualQuestionBaseRepresentation;

const Transition = React.forwardRef<unknown, TransitionProps & { children: React.ReactElement }>(
  (props, ref) => (
    <Slide direction="up" ref={ref} {...props} />
  )
);

const CreateInformationRequestModal = ({
  isOpen,
  setIsOpen,
  currentMeasurementDateId,
  currentCompanyMeasurementDateId,
  is409a,
  currentPageData,
  handleGetCurrentInformationRequests,
  currentCompanyId,
}: CreateInformationRequestModalType) => {
  const { updatePortalLinksAndTask } = useContext(InformationDashboard409AContext);
  const [isSavingModelOpen, setIsSavingModelOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [notify, setNotify] = useState(true);
  const [expirationDate, setExpirationDate] = useState<Date | null>(
    getDefaultInformationRequestExpirationDate()
  );

  const [{ companyInfo, firmInfo, user }] = useStore() as unknown as UseStoreValues;
  const { informationRequestStatuses, setUpdateTaskList, currentInformationRequests, hasLoaded }
    = useInformationRequestStatuses({
      currentCompany: companyInfo as IStandaloneCompaniesPermissions | undefined,
      currentMeasurementDateId,
      currentCompanyMeasurementDateId,
      is409a,
      reFetch: isOpen,
    });
  const shouldGetFirmUsers = !ALLOWED_LIMITED_VIEWER_ROLES_FOR_DOCS.includes(
    (user?.firms_permissions as unknown as UserFirmPermission[])?.find(permission => permission.id === firmInfo?.id)?.user_firm_role ?? 0
  );
  const { data: firmUsers = [] } = useGetFirmUsersSlim(shouldGetFirmUsers ? firmInfo?.id : undefined, '');
  const { data: companyUsers = [] } = useGetCompanyUsers(currentCompanyId, '');
  // Company Measurement Date
  const { data: reportDate } = useGetCompanyMeasurementDateByMD({
    measurementDate: {
      md_id: currentMeasurementDateId,
    },
  });

  const handleClose = useCallback(() => {
    setHasSaved(false);
    setIsSavingModelOpen(false);
    setIsOpen(false);
  }, [setIsOpen]);

  const responsibleUsers = useMemo<Array<ResponsibleUser>>(
    () => mergeResponsibleUsers(companyUsers, firmUsers),
    [companyUsers, firmUsers]
  );
  const [availableResponsibleUsers, setAvailableResponsibleUsers] = useState<Array<ResponsibleUser>>(responsibleUsers);

  useEffect(() => {
    setAvailableResponsibleUsers(responsibleUsers);
  }, [responsibleUsers]);

  const getPrimaryContact = useCallback(
    () => availableResponsibleUsers.find(userParam => userParam.isPrimaryContact)?.value,
    [availableResponsibleUsers]
  );

  const getCurrentInformationRequestResponsibleUser = useCallback(() => {
    if (currentInformationRequests.length === 0 || currentInformationRequests[0].responsible_users.length === 0) {
      return undefined;
    }
    return currentInformationRequests[0].responsible_users[0].id;
  }, [currentInformationRequests]);

  const getResponsibleUserId = useCallback(
    () => getPrimaryContact() ?? getCurrentInformationRequestResponsibleUser(),
    [getCurrentInformationRequestResponsibleUser, getPrimaryContact]
  );

  const [responsibleUserId, setResponsibleUserId] = useState<number | undefined>(getResponsibleUserId());

  const responsibleUser = useMemo(
    () => availableResponsibleUsers.find(candidate => candidate.value === responsibleUserId),
    [availableResponsibleUsers, responsibleUserId]
  );

  useEffect(() => {
    if (is409a) {
      setResponsibleUserId(getResponsibleUserId());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!responsibleUserId) {
      setResponsibleUserId(getResponsibleUserId());
    }
  }, [getResponsibleUserId, responsibleUserId]);

  const closeSaveModel = useCallback(() => {
    setIsSavingModelOpen(false);
  }, []);

  // Prefill from the current request when the confirm dialog opens, not on later refreshes.
  useEffect(() => {
    if (!isSavingModelOpen) {
      return;
    }
    const existing = getInformationRequestExpirationFromRequests(currentInformationRequests);
    setExpirationDate(
      existing
        ? parseInformationRequestExpirationDate(existing)
        : getDefaultInformationRequestExpirationDate()
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSavingModelOpen]);

  const hasCurrentInformationRequests = useMemo(
    () => currentInformationRequests.length === 2,
    [currentInformationRequests]
  );

  // find the category 1 currentInformationRequests and make a map of the progress property, which is a list of objects with ids
  // then do the same with category 2 requests
  // then filter out the questions and files that match ids with the progress property
  const createdFileIdSet = useMemo(
    () =>
      currentInformationRequests
        .filter(request => request.category === DOCUMENT_REQUEST_CATEGORY)
        .map(request => request.progress)
        .flat()
        .reduce((acc, progress) => {
          acc.add(progress.id);
          return acc;
        }, new Set()),
    [currentInformationRequests]
  );

  const createdQuestionIdSet = useMemo(
    () =>
      currentInformationRequests
        .filter(request => request.category === QUESTIONNAIRE_REQUEST_CATEGORY)
        .map(request => request.progress)
        .flat()
        .reduce((acc, progress) => {
          acc.add(progress.id);
          return acc;
        }, new Set()),
    [currentInformationRequests]
  );

  //   when we start doing other types of valuations we will make change here
  const primaryInformationRequest = useMemo(
    () => (informationRequestStatuses.length ? informationRequestStatuses[0] : undefined),
    [informationRequestStatuses]
  );

  const filterItems = useCallback(
    <T extends RequestItem>(items: T[] | undefined, createdItemIdSet: Set<unknown>): T[] | undefined => {
      if (!hasLoaded) {
        return undefined; // return undefined to render the skeleton loader
      }
      return items?.filter(item => item.id && !createdItemIdSet.has(item.id)) ?? [];
    },
    [hasLoaded]
  );

  const questions = useMemo<IndividualQuestionBaseRepresentation[] | undefined>(
    () => filterItems(primaryInformationRequest?.questions, createdQuestionIdSet),
    [primaryInformationRequest, createdQuestionIdSet, filterItems]
  );

  const files = useMemo<RequestedFile[] | undefined>(
    () => filterItems(primaryInformationRequest?.files, createdFileIdSet),
    [primaryInformationRequest, createdFileIdSet, filterItems]
  );

  const hasPendingRequestItems = Boolean(questions?.length || files?.length);
  const hasPrimaryRequestItems = Boolean(
    primaryInformationRequest?.questions?.length || primaryInformationRequest?.files?.length
  );
  // Newly added items can exist on the source request before the filtered pending lists refresh.
  const hasRequestItems = hasPendingRequestItems || (hasCurrentInformationRequests && hasPrimaryRequestItems);
  const confirmationQuestions: IndividualQuestionBaseRepresentation[] | undefined =
    questions?.length || !hasCurrentInformationRequests ? questions : primaryInformationRequest?.questions;
  const confirmationFiles: RequestedFile[] | undefined =
    files?.length || !hasCurrentInformationRequests ? files : primaryInformationRequest?.files;

  //   convert reportDate to type ReportDate
  const typedReportDate = useMemo<ReportDate>(
    () =>
      ({
        ...reportDate,
        company: reportDate?.company?.id as number,
        measurement_date: reportDate?.measurement_date as MeasurementDate,
      } as ReportDate),
    [reportDate]
  );

  const handleSave = useCallback(async () => {
    if (!primaryInformationRequest?.id || !isValidInformationRequestExpirationDate(expirationDate)) {
      return;
    }
    setIsSaving(true);

    try {
      const expirationIso = toInformationRequestExpirationIso(expirationDate);
      if (hasCurrentInformationRequests) {
        await manageApiRequest(
          ApiService.apiUpdateInformationRequestStatusUpdate(primaryInformationRequest.id.toString(), {
            id: primaryInformationRequest.id,
            responsible_users: responsibleUserId ? [responsibleUserId] : [],
            send_email: notify,
            expiration_date: expirationIso,
          })
        );
      } else if (responsibleUserId) {
        await manageApiRequest(
          ApiService.apiCreateInformationRequestUpdate(primaryInformationRequest.id.toString(), {
            responsible_users: [responsibleUserId],
            send_email: notify,
            expiration_date: expirationIso,
          })
        );
      }

      setHasSaved(true);
      setUpdateTaskList(true);

      // Update portal links and task after creating/updating request
      await updatePortalLinksAndTask();

      handleGetCurrentInformationRequests?.();
    } catch (error) {
      Logger.error('Error saving information request:', error);
    } finally {
      setIsSaving(false);
    }
  }, [
    primaryInformationRequest,
    expirationDate,
    setUpdateTaskList,
    hasCurrentInformationRequests,
    responsibleUserId,
    notify,
    handleGetCurrentInformationRequests,
    updatePortalLinksAndTask,
  ]);

  return (
    <Dialog fullScreen open={isOpen} onClose={handleClose} TransitionComponent={Transition}>
      <InformationRequestModalAppBar
        responsibleUserId={responsibleUserId}
        hasRequestItems={hasRequestItems}
        handleClose={handleClose}
        setIsSavingModelOpen={setIsSavingModelOpen}
        hasCurrentInformationRequests={hasCurrentInformationRequests}
        isSaving={isSaving}
      />
      <Container maxWidth={false} sx={{ maxWidth: '1920px' }}>
        <RequestsContent
          documents={files}
          questions={questions}
          setUpdateTaskList={setUpdateTaskList}
          primaryInformationRequestId={primaryInformationRequest?.id}
          isInitialSetup
          currentPageData={currentPageData}
        />
        <SourceResponsibleSelector
          responsibleUser={responsibleUserId}
          setResponsibleUser={setResponsibleUserId}
          responsibleUsers={availableResponsibleUsers}
          onResponsibleUsersReload={setAvailableResponsibleUsers}
          currentFirmId={firmInfo?.id}
          currentCompanyId={currentCompanyId}
          currentCompanyInfo={{
            company_id: companyInfo?.id,
            company_name: companyInfo?.name,
          } as CompanyByFirm}
          is409a={is409a}
          setIsSaving={setIsSaving}
          isDisabled={isSaving}
        />
      </Container>
      {reportDate && (
        <ConfirmRequest
          open={isSavingModelOpen}
          onClose={closeSaveModel}
          saving={isSaving}
          hasSaved={hasSaved}
          reportDate={typedReportDate}
          responsibleUser={responsibleUser}
          files={confirmationFiles}
          questions={confirmationQuestions}
          notify={notify}
          setNotify={setNotify}
          onSend={handleSave}
          onFinish={handleClose}
          expirationDate={expirationDate}
          setExpirationDate={setExpirationDate}
        />
      )}
    </Dialog>
  );
};

export default CreateInformationRequestModal;
